import React, { useEffect, useState } from 'react';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import TemplatedMenuPage from './TemplatedMenuPage';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8081';

const PLATFORM_DOMAINS = [
  'localhost',
  '127.0.0.1',
  'vendorwebstaging.trivanahospitality.com',
  'vendorweb.trivanahospitality.com',
  'staging.trivanamerchant.com',
  'trivanamerchant.com'
];

const generateSlug = (name) => {
  if (!name) return 'shop';
  return name
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_-]+/g, '-')
    .replace(/^-+|-+$/g, '');
};

const ShopTemplatedWrapper = () => {
  const { shopSlug, shopCode } = useParams();
  const history = useHistory();
  const location = useLocation();

  const [loading, setLoading] = useState(true);
  const [shopId, setShopId] = useState(null);
  const [domainError, setDomainError] = useState(null);
  const [customDomainInfo, setCustomDomainInfo] = useState(null);

  useEffect(() => {
    let isMounted = true;

    const resolveStore = async () => {
      try {
        setLoading(true);
        setDomainError(null);

        // 1. Detect if running under a custom domain (or testing custom domain via query param)
        const currentHostname = window.location.hostname.toLowerCase();
        const searchParams = new URLSearchParams(location.search);
        const testDomainParam = searchParams.get('custom_domain') || searchParams.get('domain');

        const isPlatform = PLATFORM_DOMAINS.some(p => currentHostname === p || currentHostname.endsWith('.' + p));
        const effectiveDomain = testDomainParam ? testDomainParam.trim().toLowerCase() : (!isPlatform ? currentHostname : null);

        // CASE A: Custom Domain Resolution
        if (effectiveDomain) {
          try {
            const resp = await axios.get(`${API_BASE_URL}/api/vendor/custom-domains/resolve?domain=${encodeURIComponent(effectiveDomain)}`);
            if (resp?.data?.success && resp.data.data) {
              const shop = resp.data.data;
              if (isMounted) {
                setShopId(shop.shopId || shop.restaurantId);
                setCustomDomainInfo(shop);
              }
              return;
            } else {
              if (isMounted) setDomainError(`No active store found connected to domain: ${effectiveDomain}`);
              return;
            }
          } catch (err) {
            console.error('Custom domain resolution error:', err);
            const msg = err.response?.data?.message || err.response?.data?.error || `Domain "${effectiveDomain}" is not verified or active.`;
            if (isMounted) setDomainError(msg);
            return;
          }
        }

        // CASE B: Standard Path-based Slug/Code Resolution (/:shopSlug/:shopCode or /:shopCode)
        const actualShopCode = shopCode ? shopCode : shopSlug;

        if (actualShopCode) {
          const resp = await axios.get(`${API_BASE_URL}/api/vendor/shops/code/${encodeURIComponent(actualShopCode)}`);
          if (resp?.data?.success && resp.data.data) {
            const shop = resp.data.data;
            const expectedSlug = generateSlug(shop.shopName);

            // Redirect to canonical slug URL if mismatch and slug was provided
            if (shopSlug && shopSlug !== expectedSlug) {
              history.replace(`/${expectedSlug}/${actualShopCode}`);
              return;
            }

            if (isMounted) setShopId(shop.shopId || shop.id);
          } else {
            toast.error('Shop not found');
            if (isMounted) setDomainError('Shop not found for code: ' + actualShopCode);
          }
        } else {
          // CASE C: Root of platform domain without shop slug/code
          if (isMounted) {
            setDomainError('Welcome to Trivana Storefront Platform. Please access your store via your unique store URL or connected custom domain.');
          }
        }
      } catch (error) {
        console.error('Failed to load shop', error);
        if (isMounted) setDomainError('Unable to load storefront. Please verify your connection.');
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    resolveStore();
    return () => { isMounted = false; };
  }, [shopSlug, shopCode, location.search, history]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="text-center space-y-3">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="text-sm font-medium text-slate-600">Loading your storefront...</p>
        </div>
      </div>
    );
  }

  if (domainError || !shopId) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl border border-slate-200 p-6 md:p-8 text-center space-y-4">
          <div className="w-16 h-16 rounded-full bg-indigo-50 text-indigo-600 flex items-center justify-center text-2xl mx-auto font-bold">
            🌐
          </div>
          <div>
            <h2 className="text-xl font-bold text-slate-900">Storefront Not Available</h2>
            <p className="text-sm text-slate-600 mt-2 leading-relaxed">
              {domainError || 'This domain is not yet connected to an active storefront or DNS verification is pending.'}
            </p>
          </div>

          <div className="pt-2 text-xs text-slate-400 border-t border-slate-100">
            If you are the store owner, please check your <strong className="text-slate-600">Profile Settings → Connect Domain</strong> tab in the merchant admin panel to ensure DNS records are verified.
          </div>
        </div>
      </div>
    );
  }

  const isRestaurantOutlet = customDomainInfo?.outletType === 'RESTAURANT' || Boolean(customDomainInfo?.restaurantId && !customDomainInfo?.shopId);

  return (
    <TemplatedMenuPage 
      shopMode={!isRestaurantOutlet} 
      shopId={!isRestaurantOutlet ? shopId : undefined} 
      restaurantId={isRestaurantOutlet ? shopId : undefined}
      customDomain={customDomainInfo?.customDomain}
      initialShop={customDomainInfo}
    />
  );
};

export default ShopTemplatedWrapper;
